import { Button, Combobox, Header, Segment, SingleDatePicker } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { ModeReglementContext, RegieContext } from '@/contexts';
import { getBordereauVersementVolatile } from '@/routes';
import { validerPeriodeEtatRecapitulatif } from '@/utils';
import { useRegieStore } from '@/zustand';

dayjs.locale('fr');

const REGISSEUR_TOUS = 'tous';

const EtatsControle = () => {
  const regie = useRegieStore((state) => state.regie);
  const { regies } = React.useContext(RegieContext);
  const { regieModesReglement } = React.useContext(ModeReglementContext);

  const [dateDebut, setDateDebut] = React.useState<dayjs.Dayjs | null>(null);
  const [dateFin, setDateFin] = React.useState<dayjs.Dayjs | null>(null);
  const [regisseurValue, setRegisseurValue] = React.useState<string>(REGISSEUR_TOUS);
  const [modeReglement, setModeReglement] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  const regisseurOptions = React.useMemo(() => {
    const regieComplete = _.find(regies, { identifiant: regie?.identifiant });
    const regisseursActifs = (regieComplete?.regisseurs ?? []).filter((r) => r.actif);
    return [
      { key: REGISSEUR_TOUS, value: REGISSEUR_TOUS, text: 'Tous les régisseurs' },
      ...regisseursActifs.map((r) => ({
        key: String(r.identifiant),
        value: String(r.identifiant),
        text: `${r.prenom} ${r.nom}`,
      })),
    ];
  }, [regies, regie?.identifiant]);

  const modeReglementOptions = React.useMemo(
    () =>
      (regieModesReglement ?? []).map((m) => ({
        key: m.code,
        value: m.code,
        text: m.libelle,
      })),
    [regieModesReglement],
  );

  const handleEdit = React.useCallback(async () => {
    const validation = validerPeriodeEtatRecapitulatif(dateDebut, dateFin);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }
    if (!modeReglement) {
      toast.error('Veuillez sélectionner un mode de règlement.');
      return;
    }

    setLoading(true);
    try {
      const response = (await request.get(
        getBordereauVersementVolatile({
          dateDebut: dateDebut.format('YYYY-MM-DD'),
          dateFin: dateFin.format('YYYY-MM-DD'),
          identifiantRegisseur: regisseurValue === REGISSEUR_TOUS ? null : Number(regisseurValue),
          codesModesReglement: [modeReglement],
        }),
        { headers: { Accept: 'application/json, application/pdf' } },
      )) as any;

      if (response?.url) {
        window.open(response.url, '_blank');
      } else {
        const blob = response?.data ?? response;
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
    } catch (e: any) {
      toast.error(e?.message?.errors ?? 'Erreur lors de la génération du bordereau.');
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, regisseurValue, modeReglement]);

  const disabledDateDebut = React.useCallback(
    (d: dayjs.Dayjs) => d.year() < 2025 || d.year() > 2026 || d.isAfter(dayjs(), 'day'),
    [],
  );

  const disabledDateFin = React.useCallback(
    (d: dayjs.Dayjs) =>
      d.year() < 2025 ||
      d.year() > 2026 ||
      d.isAfter(dayjs(), 'day') ||
      !!dateDebut?.isAfter(d, 'day'),
    [dateDebut],
  );

  const handleDateDebutChange = React.useCallback(
    (val: dayjs.Dayjs | null) => {
      const next = val && disabledDateDebut(val) ? null : val;
      setDateDebut(next);
      if (next) {
        setDateFin((current) => (current?.isBefore(next, 'day') ? null : current));
      }
    },
    [disabledDateDebut],
  );

  const handleDateFinChange = React.useCallback(
    (val: dayjs.Dayjs | null) => {
      setDateFin(val && disabledDateFin(val) ? null : val);
    },
    [disabledDateFin],
  );

  const isFormComplete = !!dateDebut && !!dateFin && !!modeReglement;

  return (
    <Segment
      inverted
      secondary
      style={{
        flex: 1,
        overflow: 'auto',
        background: 'transparent',
      }}
    >
      <Segment
        style={{
          borderRadius: 14,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Header as="h1" textAlign="center" style={{ marginBottom: '1em' }}>
          <Header.Content>Etats de contrôle</Header.Content>
        </Header>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: '2em',
            marginBottom: '1.5em',
            padding: '0 1em',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Header as="h4" style={{ marginBottom: '0.5em' }}>
              Période
            </Header>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <span>Du</span>
              <SingleDatePicker
                clearable
                inputReadOnly
                changeOnBlur={false}
                value={dateDebut}
                onChange={handleDateDebutChange}
                disabledDate={disabledDateDebut}
              />
              <span>au</span>
              <SingleDatePicker
                clearable
                inputReadOnly
                changeOnBlur={false}
                value={dateFin}
                onChange={handleDateFinChange}
                disabledDate={disabledDateFin}
              />
            </div>
          </div>

          <div style={{ minWidth: 280 }}>
            <Header as="h4" style={{ marginBottom: '0.5em' }}>
              Sélection des encaissements / annulations réalisés par
            </Header>
            {/* @ts-expect-error */}
            <Combobox
              menuPortalTarget={document.body}
              selection
              options={regisseurOptions}
              value={regisseurValue}
              onChange={(_e: unknown, { value }: { value: string }) => setRegisseurValue(value)}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ minWidth: 360 }}>
            <Header as="h4" style={{ marginBottom: '0.5em' }}>
              Pour le mode de règlement
            </Header>
            {/* @ts-expect-error */}
            <Combobox
              menuPortalTarget={document.body}
              selection
              placeholder="Sélectionnez un mode de règlement"
              options={modeReglementOptions}
              value={modeReglement ?? ''}
              onChange={(_e: unknown, { value }: { value: string }) => setModeReglement(value)}
              style={{ minWidth: 320 }}
            />
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '1em',
            paddingRight: '1em',
            gap: '1em',
          }}
        >
          <Button
            positive
            size="large"
            content="Éditer"
            loading={loading}
            disabled={!isFormComplete || loading}
            onClick={handleEdit}
          />
        </div>
      </Segment>
    </Segment>
  );
};

export default EtatsControle;
